import { Box, Card, CardActionArea, Stack, cardActionAreaClasses } from '@mui/material';
import { type HandleType, Position } from '@xyflow/react';

import { Text, colors, isDefined } from 'common';
import { BlockCategory } from 'vivaa-api';

import { NodeBody } from './Body';
import { NodeHandle } from './Handles/Handle';
import { HANDLE_TOP_OFFSET_IN_PIXELS, NODE_WIDTH_IN_PIXELS } from './cardDimensions';
import { BlockCategoryIcon } from '../../../../BlockIcons/BlockCategories';
import { EdgeHandleType } from '../../../../types';
import { useReactFlowViewContext } from '../../../context/context';
import { UiPlaceholderBlockType } from '../../../types';
import {
  REACT_FLOW_CARD_BORDER_RADIUS,
  REACT_FLOW_CARD_DEFAULT_BOX_SHADOW,
  REACT_FLOW_CARD_GROUPED_BOX_SHADOW,
  REACT_FLOW_CARD_HIGHLIGHTED_BOX_SHADOW,
} from '../constants';
import { type ReactFlowPlaceholderNodeProps } from './types';

const PLACEHOLDER_HANDLE_BY_UI_NODE_TYPE: Record<UiPlaceholderBlockType, HandleType[]> = {
  [UiPlaceholderBlockType.BasePlaceholder]: ['target'],
  [UiPlaceholderBlockType.Placeholder]: ['target', 'source'],
};

export function PlaceholderNodeCard(props: ReactFlowPlaceholderNodeProps) {
  const {
    id: nodeId,
    selected: isReactFlowNodeSelected,
    parentId,
    data: { uiBlockType },
  } = props;
  const { setSelectedNodeId, selectedNodeId } = useReactFlowViewContext();
  const isSelected = nodeId === selectedNodeId || isReactFlowNodeSelected;

  return (
    <Box>
      <Card
        variant="outlined"
        sx={{
          borderRadius: REACT_FLOW_CARD_BORDER_RADIUS,
          border: '1px solid',
          borderStyle: 'dashed',
          borderColor: colors.primary.dark,
          boxShadow: isSelected
            ? REACT_FLOW_CARD_HIGHLIGHTED_BOX_SHADOW
            : isDefined(parentId)
              ? REACT_FLOW_CARD_GROUPED_BOX_SHADOW
              : REACT_FLOW_CARD_DEFAULT_BOX_SHADOW,
          /**
           * Our node handles extend outside of the card, so we need to allow the card to not hide overflow.
           * Overflow needs to be handled in each part of the node card.
           */
          overflow: 'unset',
        }}
      >
        <CardActionArea
          onClick={() => {
            setSelectedNodeId(nodeId);
          }}
          sx={{
            [`&:hover > .${cardActionAreaClasses.focusHighlight}`]: {
              // Hide the focus highlight overlay on node cards.
              opacity: 0,
            },
          }}
        >
          <Stack
            gap={1}
            sx={{
              width: NODE_WIDTH_IN_PIXELS,
              paddingY: 1,
            }}
          >
            <Stack marginX={2} gap={1}>
              <Stack gap={1} alignItems="start" sx={{ overflow: 'hidden' }}>
                <Stack
                  direction="row"
                  gap={1}
                  sx={{
                    justifyContent: 'left',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <BlockCategoryIcon blockCategory={BlockCategory.Placeholder} variant="large" />{' '}
                  <Box flex={1}>
                    <Text variant="subtitle2" color="inherit">
                      Placeholder
                    </Text>
                    <Text variant="subtitle2" color="inherit" bold>
                      Choose a Block
                    </Text>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
            {PLACEHOLDER_HANDLE_BY_UI_NODE_TYPE[uiBlockType].map((handleType) => {
              return (
                <NodeHandle
                  key={handleType}
                  type={handleType}
                  position={handleType === 'target' ? Position.Left : Position.Right}
                  style={{
                    /**
                     * This property is related to snap-to-grid behavior and placement of new nodes.
                     * @see NodeHandlePlacementUsage
                     */
                    top: HANDLE_TOP_OFFSET_IN_PIXELS,
                  }}
                  nodeProps={props}
                  edgeHandleType={EdgeHandleType.Default}
                />
              );
            })}
            <NodeBody nodeProps={props} nodeHandles={[]} />
          </Stack>
        </CardActionArea>
      </Card>
    </Box>
  );
}
